import React, { useState, useCallback, useEffect, useMemo } from 'react';
import * as R from 'ramda';
import intl from 'react-intl-universal';
import classNames from 'classnames';
import { MenuItem, Position } from '@blueprintjs/core';
import { Suggest } from '@blueprintjs/select';

import { CLASSES } from '@/constants/classes';
import { DialogsName } from '@/constants/dialogs';

import { MenuItemNestedText } from '@/components/Menu/MenuItemNestedText';
import { FormattedMessage as T } from '@/components/FormattedMessage';
import { nestedArrayToflatten, filterAccountsByQuery } from '@/utils';
import {
  accountPredicate,
  getAccountDisplayName,
  compareByAccountCode,
  isAccountCodeQuery,
} from './_components';

import withDialogActions from '@/containers/Dialog/withDialogActions';
import { registerAccountCreatedCallback } from '@/containers/Dialogs/AccountDialog/accountDialogCallbacks';

// Create new account renderer.
const createNewItemRenderer = (query: any, active: any, handleClick: any) => {
  return (
    <MenuItem
      icon="add"
      text={intl.get('list.create', { value: `"${query}"` })}
      active={active}
      onClick={handleClick}
    />
  );
};

// Create new item from the given query string.
//
// Une requête purement numérique est un CODE de compte, jamais un libellé —
// c'est déjà ainsi que la recherche l'interprète (`isAccountCodeQuery`, match
// par préfixe de code). La pré-remplir comme NOM créait des comptes SANS code,
// nommés d'après leur propre code (« 614001 », code vide) : illisibles
// (rendus « - 614001 ») et introuvables aux recherches suivantes par code.
export const createNewAccountFromQuery = (query: any) => {
  const value = String(query ?? '').trim();

  return isAccountCodeQuery(value)
    ? { code: value, name: '' }
    : { name: value };
};

// Handle input value renderer.
const handleInputValueRenderer = (inputValue: any) => {
  if (inputValue) {
    return getAccountDisplayName(inputValue).toString();
  }
  return '';
};

// Filters accounts items (shared predicate: numeric query → code prefix match).
const filterAccountsPredicater = accountPredicate;

/**
 * Accounts suggest field.
 */
function AccountsSuggestFieldRoot({
  // #withDialogActions
  openDialog,

  // #ownProps
  accounts,
  initialAccountId,
  selectedAccountId,
  defaultSelectText = intl.formatMessage({ id: 'select_account' }),
  popoverFill = false,
  popoverProps: customPopoverProps,
  onAccountSelected,

  filterByParentTypes = [],
  filterByTypes = [],
  filterByNormal,
  filterByRootTypes = [],

  allowCreate,
  openOnKeyDown = false,

  ...suggestProps
}: any) {
  const flattenAccounts = useMemo(
    () => nestedArrayToflatten(accounts),
    [accounts],
  );

  // Filters accounts based on filter props.
  const filteredAccounts = useMemo(() => {
    let filteredAccounts = filterAccountsByQuery(flattenAccounts, {
      filterByRootTypes,
      filterByParentTypes,
      filterByTypes,
      filterByNormal,
    });
    // F12 — l'aplatissement de l'arbre restituait un ordre arbitraire :
    // « 61 » ramenait 6197, 6183, 6131, 61311… et « 6111 » plaçait le
    // sous-compte 611104 AVANT son parent 6111. Le comptable lit un plan
    // comptable dans l'ordre des codes : on trie par code croissant, en
    // comparant segment par segment pour qu'un collectif précède toujours
    // ses sous-comptes (6111 < 611104 < 6112).
    return [...filteredAccounts].sort(compareByAccountCode);
  }, [
    flattenAccounts,
    filterByRootTypes,
    filterByParentTypes,
    filterByTypes,
    filterByNormal,
  ]);

  // Find initial account object to set it as default account in initial render.
  const initialAccount = useMemo(
    () => filteredAccounts.find((a: any) => a.id === initialAccountId),
    [initialAccountId, filteredAccounts],
  );

  const [selectedAccount, setSelectedAccount] = useState(
    initialAccount || null,
  );

  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (typeof selectedAccountId !== 'undefined') {
      const account = selectedAccountId
        ? filteredAccounts.find((a: any) => a.id === selectedAccountId)
        : null;
      setSelectedAccount(account);
    }
  }, [selectedAccountId, filteredAccounts, setSelectedAccount]);

  // Handle input click to open dropdown
  const handleInputClick = useCallback(() => {
    setIsOpen(true);
  }, []);

  // Account item of select accounts field.
  // F12 — le code du compte était rendu DEUX FOIS par ligne : dans le libellé
  // de gauche (« 6111 - Achats de marchandises », via getAccountDisplayName)
  // et de nouveau dans la colonne `label` de droite. Une seule occurrence
  // suffit ; on garde celle de gauche, en tête de ligne, qui est celle que le
  // comptable lit et sur laquelle la liste est triée.
  const accountItem = useCallback(
    (item: any, { handleClick, modifiers, query }: any) => {
      return (
        <MenuItem
          text={
            <MenuItemNestedText
              level={item.level}
              text={getAccountDisplayName(item)}
            />
          }
          key={item.id}
          onClick={handleClick}
        />
      );
    },
    [],
  );

  const selectAccount = useCallback(
    (account: any) => {
      setSelectedAccount({ ...account });
      onAccountSelected && onAccountSelected(account);
    },
    [setSelectedAccount, onAccountSelected],
  );

  const handleAccountSelect = useCallback(
    (account: any) => {
      if (account.id) {
        selectAccount(account);
      } else {
        // "Create «query»" was picked. Open the account dialog pre-filled with
        // the typed value — dans le bon champ : le CODE quand la saisie est
        // numérique, le NOM sinon — and register a one-shot callback so the
        // freshly created account is auto-selected back into this field.
        const typedName = account?.name || '';
        const typedCode = account?.code || '';
        const onCreatedKey = registerAccountCreatedCallback(
          (createdAccount: any) => {
            if (createdAccount?.id) {
              selectAccount(createdAccount);
            }
          },
        );
        openDialog(DialogsName.AccountForm, {
          name: typedName,
          code: typedCode,
          onCreatedKey,
        });
      }
      setIsOpen(false);
    },
    [selectAccount, openDialog],
  );

  // Maybe inject new item props to select component.
  const maybeCreateNewItemRenderer = allowCreate ? createNewItemRenderer : null;
  const maybeCreateNewItemFromQuery = allowCreate
    ? createNewAccountFromQuery
    : null;

  // Merge input props with onClick handler
  const mergedInputProps = {
    placeholder: defaultSelectText,
    onClick: handleInputClick,
    ...suggestProps.inputProps,
  };

  // Merge popover props with isOpen state
  const mergedPopoverProps = {
    minimal: true,
    boundary: 'window',
    isOpen: isOpen,
    onInteraction: (nextOpenState: any) => setIsOpen(nextOpenState),
    // Elevate the suggestions above the drawer overlay. The drawer portal sits
    // at z-index 5000; without this the popover renders *under* the drawer and
    // is invisible/unclickable (see App.scss `.select-popover-in-drawer`).
    popoverClassName: 'select-popover-in-drawer',
    // F12 — la liste basculait VERS LE HAUT dès que la ligne de saisie
    // approchait du bas de la fenêtre, recouvrant tout l'en-tête de l'écriture
    // en cours (date, journal, référence, devise). Popper « retournait » le
    // popover faute de place sous le champ. On désactive ce retournement : la
    // liste s'ouvre toujours SOUS le champ, sa hauteur étant déjà plafonnée à
    // 300 px avec défilement interne par `blueprint-select.css`.
    position: Position.BOTTOM_LEFT,
    modifiers: {
      flip: { enabled: false },
      preventOverflow: { enabled: true, boundariesElement: 'viewport' },
    },
    ...customPopoverProps,
  };

  // Get the default active item (first filtered item or selected item)
  const defaultActiveItem =
    selectedAccount ||
    (filteredAccounts.length > 0 ? filteredAccounts[0] : null);

  return (
    <Suggest
      items={filteredAccounts}
      noResults={<MenuItem disabled={true} text={<T id={'no_accounts'} />} />}
      itemRenderer={accountItem}
      itemPredicate={filterAccountsPredicater}
      onItemSelect={handleAccountSelect}
      selectedItem={selectedAccount}
      activeItem={defaultActiveItem}
      inputProps={mergedInputProps}
      resetOnClose={true}
      fill={true}
      popoverProps={mergedPopoverProps}
      openOnKeyDown={openOnKeyDown}
      inputValueRenderer={handleInputValueRenderer}
      className={classNames(CLASSES.FORM_GROUP_LIST_SELECT, {
        [CLASSES.SELECT_LIST_FILL_POPOVER]: popoverFill,
      })}
      createNewItemRenderer={maybeCreateNewItemRenderer}
      createNewItemFromQuery={maybeCreateNewItemFromQuery}
      scrollToActiveItem={false}
      {...suggestProps}
    />
  );
}

export const AccountsSuggestField = R.compose(withDialogActions)(
  AccountsSuggestFieldRoot,
);
